import React, { useState } from 'react';
import { Eye, Download, Share, TrendingUp, BarChart3, PieChart, FileText, Zap, Clock, Star } from 'lucide-react';

interface Insight {
  id: string;
  title: string;
  description: string;
  type: 'trend' | 'correlation' | 'anomaly' | 'prediction';
  confidence: number;
  data?: any;
  createdAt: Date;
}

interface InsightViewerProps {
  insights?: Insight[];
  className?: string;
  onGenerateDeepReport?: (insights: Insight[]) => void;
  onExportInsight?: (insight: Insight) => void;
  onExportAllInsights?: () => void;
}

const InsightViewer: React.FC<InsightViewerProps> = ({ 
  insights = [],
  className = '',
  onGenerateDeepReport,
  onExportInsight,
  onExportAllInsights
}) => {
  const [selectedInsight, setSelectedInsight] = useState<Insight | null>(null);
  const [selectedInsights, setSelectedInsights] = useState<Set<string>>(new Set());

  const getTypeIcon = (type: Insight['type']) => {
    switch (type) {
      case 'trend': return <TrendingUp className="w-4 h-4" />;
      case 'correlation': return <BarChart3 className="w-4 h-4" />;
      case 'anomaly': return <PieChart className="w-4 h-4" />;
      case 'prediction': return <TrendingUp className="w-4 h-4" />;
    }
  };

  const getTypeColor = (type: Insight['type']) => {
    return 'text-theme-secondary bg-theme-hover border border-theme';
  };

  const getConfidenceColor = (confidence: number) => {
    // Sobre : toujours noir sur gris
    return 'text-theme-text-primary bg-theme-hover';
  };

  // Utiliser seulement les insights réels, pas de mock data
  const displayInsights: Insight[] = insights;

  const handleToggleInsight = (insightId: string) => {
    setSelectedInsights(prev => {
      const newSet = new Set(prev);
      if (newSet.has(insightId)) {
        newSet.delete(insightId);
      } else {
        newSet.add(insightId);
      }
      return newSet;
    });
  };

  const handleGenerateDeepReport = () => {
    const selected = displayInsights.filter(insight => selectedInsights.has(insight.id));
    if (selected.length > 0 && onGenerateDeepReport) {
      onGenerateDeepReport(selected);
    }
  };

  return (
    <div className={`insight-viewer ${className}`}>
      <div className="flex items-end justify-between mb-10 gap-6">
        <div>
          <p className="page-kicker mb-3">Découvertes</p>
          <h3 className="page-title">Insights</h3>
          <p className="text-sm text-theme-secondary mt-2">
            {displayInsights.length} générés · {selectedInsights.size} sélectionnés
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          {selectedInsights.size > 0 && (
            <button 
              onClick={handleGenerateDeepReport}
              className="btn-modern btn-primary"
            >
              <FileText className="w-4 h-4" />
              <span>Rapport ({selectedInsights.size})</span>
            </button>
          )}
          
          {onExportAllInsights && (
            <button 
              onClick={onExportAllInsights}
              className="btn-modern btn-secondary"
            >
              <Download className="w-4 h-4" />
              <span>Export</span>
            </button>
          )}
        </div>
      </div>

      {selectedInsights.size > 0 && (
        <div className="px-5 py-4 border border-theme mb-6 bg-theme-surface">
          <p className="page-kicker mb-1">Sélection</p>
          <div className="font-display text-3xl">{selectedInsights.size}</div>
        </div>
      )}

      <div className="space-y-px overflow-y-auto max-h-[70vh] border border-theme">
        {displayInsights.map((insight) => {
          const isSelected = selectedInsights.has(insight.id);
          
          return (
            <div 
              key={insight.id} 
              className={`group relative p-5 cursor-pointer bg-theme-surface ${
                isSelected 
                  ? 'border-l-2' 
                  : 'border-l-2 border-transparent hover:bg-theme-hover'
              }`}
              style={isSelected ? { borderLeftColor: 'var(--spark-gold)' } : undefined}
              onClick={() => handleToggleInsight(insight.id)}
            >
              
              <div className="absolute top-4 right-4">
                <div className={`w-4 h-4 border flex items-center justify-center ${
                  isSelected 
                    ? 'bg-[#14110E] border-[#14110E] dark:bg-[#F3EEE4] dark:border-[#F3EEE4]' 
                    : 'border-theme-strong bg-transparent'
                }`}>
                  {isSelected && (
                    <svg className="w-2.5 h-2.5 text-[#FBF7F0] dark:text-[#14110E]" viewBox="0 0 12 12" fill="none">
                      <path d="M10 3L4.5 8.5L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>
              </div>
              
              {/* Header insight */}
              <div className="flex items-start space-x-3 mb-4">
                <div className={`p-2 rounded-lg ${getTypeColor(insight.type)}`}>
                  {getTypeIcon(insight.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-theme-primary text-lg mb-1 truncate">{insight.title}</h4>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-theme-secondary capitalize">{insight.type}</span>
                  </div>
                </div>
              </div>
              
              {/* Description - Style moderne épuré */}
              <div className="text-theme-primary text-sm leading-relaxed mb-4">
                {insight.description?.split('\n').map((line, index) => {
                  // FILTRE AMÉLIORÉ : Éliminer toutes les lignes de séparation markdown
                  const trimmedLine = line.trim();
                  
                  // Ignorer les séparateurs (lignes avec | et - ou =)
                  if (trimmedLine.includes('|') && /[-=]{2,}/.test(trimmedLine)) {
                    return null;
                  }
                  
                  // Ignorer les lignes qui ne contiennent que des tirets/pipes
                  if (/^[|\-\s=]+$/.test(trimmedLine)) {
                    return null;
                  }
                  
                  // Ligne vide
                  if (!trimmedLine) {
                    return <div key={index} className="h-2" />;
                  }
                  
                  // Tableau markdown - Rendu moderne
                  if (trimmedLine.includes('|') && trimmedLine.startsWith('|')) {
                    const cells = trimmedLine.split('|')
                      .map(cell => cell.trim())
                      .filter(cell => cell !== '' && !/^[-=\s]+$/.test(cell));
                    
                    if (cells.length === 0) return null;
                    
                    return (
                      <div key={index} className="my-3 overflow-hidden">
                        <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${cells.length}, 1fr)` }}>
                          {cells.map((cell, cellIndex) => (
                            <div 
                              key={cellIndex} 
                              className="px-3 py-2 bg-theme-hover rounded-lg border border-theme text-xs font-medium text-theme-primary"
                            >
                              {cell}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  
                  // Texte normal avec support bold markdown
                  const escapedText = trimmedLine
                    .replace(/&/g, '&amp;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;')
                    .replace(/"/g, '&quot;')
                    .replace(/'/g, '&#039;');
                  
                  const formattedText = escapedText.replace(/\*\*(.*?)\*\*/g, '<strong class="text-theme-primary font-semibold">$1</strong>');
                  
                  return <div key={index} className="mb-1 leading-relaxed" dangerouslySetInnerHTML={{ __html: formattedText }} />;
                })}
              </div>
              
              {/* Footer avec actions */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2 text-xs text-theme-secondary">
                  <Clock className="w-3 h-3" />
                  <span>{insight.createdAt ? new Date(insight.createdAt).toLocaleDateString('fr-FR') : new Date().toLocaleDateString('fr-FR')}</span>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedInsight(insight);
                    }}
                    className="p-1.5 text-theme-tertiary hover:text-theme-primary border border-transparent hover:border-theme"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  
                  {onExportInsight && (
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        onExportInsight(insight);
                      }}
                      className="p-1.5 text-theme-tertiary hover:text-theme-primary border border-transparent hover:border-theme"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
              
              {/* Effet de sélection */}
              {isSelected && (
                <div className="absolute inset-0 pointer-events-none" />
              )}
            </div>
          );
        })}
      </div>

      {displayInsights.length === 0 && (
        <div className="text-center py-16 border border-theme bg-theme-surface">
          <p className="font-display text-2xl mb-2">Aucun insight</p>
          <p className="text-sm text-theme-secondary">Ouvrez la console et lancez une analyse.</p>
        </div>
      )}

      {/* Modal pour insight détaillé */}
      {selectedInsight && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-theme-surface border border-theme p-6 max-w-2xl w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">{selectedInsight.title}</h3>
              <button 
                onClick={() => setSelectedInsight(null)}
                className="text-theme-text-secondary hover:text-theme-text-primary text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                {getTypeIcon(selectedInsight.type)}
                <span className="capitalize font-medium">{selectedInsight.type}</span>
              </div>
              
              <p className="text-theme-secondary">{selectedInsight.description}</p>
            </div>
            
            <div className="flex justify-end gap-2">
              <button 
                onClick={() => setSelectedInsight(null)}
                className="btn-modern btn-secondary"
              >
                Fermer
              </button>
              <button className="btn-modern btn-primary">
                Exporter cet insight
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InsightViewer;